import { DateTime } from "luxon";
import { cloneDeep } from "lodash-es";

import LocalForagePlugin, { products, syncState } from "@/plugins/LocalForagePlugin";
import { productsSyncKey } from "@/types/products/product-definitions";
import { mtpcProductKey, scccProductKey, tivProductKey, trwProductKey } from "@/types/permissions";

import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@/types/api/fetch";

import type { ProductApiResponse, Products } from "@/types/api/content-models";
import type { Category, Product, SubscriptionPlan } from "@/types/entities";

import { consoleLoggerFactory, getDefaultLoggerOptions, LogLevels } from "@/types/console-logger";
const logger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

export function mapProductApiResponseToProduct( response: ProductApiResponse ) {
    return aliasTivAndTrw( ( {
        id: response.id,
        slug: response.slug,
        name: response.name,
        pageTitle: response.pageTitle ?? undefined,
        subTitle: response.subTitle ?? undefined,
        body: response.body ?? undefined,
        codes: response.codes ?? undefined,
        favicon: response.favicon ?? undefined,
        logo: response.logo ?? undefined,
        logo_dark: response.logo_dark ?? undefined,
        isDisplay: response.isDisplay,
        scheduleAppointmentLink: response.scheduleAppointmentLink,
        plans: response.plans.map( ( plan: SubscriptionPlan ) => ( {
            id: plan.id,
            title: plan.title ?? undefined,
            recommended: plan.recommended,
            duration: plan.duration ?? undefined,
            numIss: plan.numIss ?? undefined,
            freq: plan.freq ?? undefined,
            desc: plan.desc ?? undefined,
            orderUrl: plan.orderUrl ?? undefined,
            topFeatures: plan.topFeatures ?? undefined
        } ) )
    } ) );
}

export async function refreshProductsFromServer(): Promise< Product[] > {
    let allProducts: Product[] = [];
    const response =
        await goFetch< Products, PersistedQueryPayload >( {
            endpoint: 'content/graphql',
            payload: {
                id: 'b328c921a2e7a12d0894090087ef59b8',
                variables: { skip: 0, limit: 25 }
            }
        } );

    if ( response?.data?.productCatalog?.products ) {
        // cache only the products that are marked as "display-able"
        allProducts =
            [ ...( response.data.productCatalog.products
                    .map( ( p: ProductApiResponse ) => mapProductApiResponseToProduct( p ) )
                    .sort( ( prod1: Product, prod2: Product ): number =>
                        ( prod1.name.toLowerCase() > prod2.name.toLowerCase() ) ? 1 :
                            ( ( prod1.name.toLowerCase() < prod2.name.toLowerCase() ) ? -1 : 0 ) )
            ) ];
    }

    return allProducts;
}

export async function loadProductMap( productsMap: Map< string, Category > ): Promise< boolean > {
    let updated = false;
    try {
        const t0: number = performance.now();

        if ( typeof products === 'undefined' || typeof syncState === 'undefined' ) {
            // Wait for the driver to settle
            LocalForagePlugin.initialize();
        }

        await syncState.ready();
        // Check for the next refresh interval
        const syncThresholdInMillis = await syncState.getItem< number >( productsSyncKey );
        if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() ) {
            
            const nextSyncThresholdInMillis = DateTime.utc().plus( { hours: 4 } ).toMillis();
            await syncState.setItem( productsSyncKey, nextSyncThresholdInMillis );

            const productsReceived: Product[] = await refreshProductsFromServer();

            if(productsReceived.length > 0)
            {
                updated = true;
                // Store the items recently retrieved
                await products.ready();
                for ( const product of productsReceived ) {
                    await products.setItem( product.id, cloneDeep( product ) );
                }

                for ( const product of productsReceived ) {
                    productsMap.set( product.id, product );
                }

                const t1: number = performance.now();
                logger.info( `[ sync-worker ] Refreshed ${ productsMap.size } products from server in ${ t1 - t0 } ms` );

                const t2: number = performance.now();
                logger.info( `[ sync-worker ] Reset cache for ${ productsMap.size } products in ${ t2 - t1 } ms` );
            }
            else
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( productsSyncKey, nextSyncThresholdInMillis );
            }
        }
        // Make sure map is empty
        productsMap.clear();

        // Load whatever is in the local cache
        await products.ready();
        // Iterate over all elements within IDB
        await products.iterate( ( product: Product ) => {
            productsMap.set( product.id, product );
            return;
        } );

    } catch ( err: any ) {
        logger.error( JSON.stringify( err.message ) );
    }

    return updated;
}

/**
 * Private mapping function to handle migrating away from TIV/TRW slugs in favor of the current MTPCC/SCCC
 * @param product
 */
function aliasTivAndTrw( product: Product ): Product {
    // Re-mapping logic to handle the poorly planned rollout of the re-branding of TIV/TRW...
    if ( product.slug.includes( tivProductKey ) || product.slug.includes( trwProductKey ) ) {
        const alternateProduct: Product = { ...product };
        alternateProduct.slug = product.slug.replace( tivProductKey, mtpcProductKey ).replace( trwProductKey, scccProductKey );
        return alternateProduct;
    }

    return product;
}
